import React from "react";
import { Save } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Divider, Grid, Switch, Typography } from "@mui/material";
import { CustomButton } from "components/CustomButtom";
import ModalComponent from "components/Modal";
import { ModalUnsavedEdit } from "components/ModalUnsavedEdit";
import SelectComponent from "components/Select";
import { FormControlCustom } from "components/StackCustom/styles";
import { ControlledTextField } from "components/TextField/ControlledTextField";
import { useNavigation } from "contexts/notificationsContext";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { getEmpresaById } from "services/modules/empresas";
import {
  getUsuarioById,
  postUsuario,
} from "services/modules/empresas/usuarios";
import { FSelect } from "shared/interfaces/select";
import { IGetUsuarioById, IPostUsuario } from "shared/interfaces/usuarios";
import { fontsSizes } from "styles/theme";

interface ModalFornecedorProps {
  isOpen: boolean;
  onClose: () => void;
  refreshData?: () => void;
  dataUsuario?: IGetUsuarioById;
}

const tipoUsuarioEnum = {
  1: "Master",
  2: "Administrador",
  3: "Usuário Geral",
  4: "Usuário MOF",
};

export function ModalCadastroUsuario({
  isOpen,
  onClose,
  dataUsuario,
}: ModalFornecedorProps) {
  const params = useParams();
  const { openNotification } = useNavigation();
  const [isOpenModalUnsaved, setIsOpenModalUnsaved] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [isAtivo, setIsAtivo] = useState(dataUsuario?.ativo || false);
  const [items, setItems] = useState<(FSelect & { disponivel: boolean })[]>([]);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [subPermissoesTarefas, setSubPermissoesTarefas] = useState<number[]>([]);

  const subPermissoesTarefasOpcoes = [
    { value: 101, label: "Criar e Editar minhas tarefas" },
    { value: 102, label: "Editar o tempo das minhas tarefas" },
    { value: 103, label: "Reabrir tarefas concluídas" },
    { value: 104, label: "Alocar outros usuários como responsável das tarefas" },
    { value: 105, label: "Editar tarefas de terceiros" },
  ];

  // Sub-permissões de Tarefas (ids 101-105) não são abas — são tratadas à parte
  const isSubPermissaoTarefas = (id: number) => id >= 101 && id <= 105;

  // As sub-permissões 101-105 só estão disponíveis quando a aba Tarefas (10) está
  // disponível/contratada para a empresa. A disponibilidade da aba 10 vem do payload
  // da empresa (item.disponivel em `items`); recurso indisponível => sub-permissões
  // desabilitadas. Default false quando a aba 10 ainda não foi carregada/não existe.
  const subPermissoesTarefasDisponiveis =
    items.find((item) => item.value === 10)?.disponivel ?? false;

  const handleSubPermissaoChange = (value: number) => {
    setSubPermissoesTarefas((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };
  const tipoUsuarioEnumArray = Object.entries(tipoUsuarioEnum).map(
    ([value, label]) => ({
      value: Number(value),
      label,
    })
  );
  const {
    control,
    getValues,
    handleSubmit,
    setValue,
    watch,
    reset,
    clearErrors,
    formState,
    ...methods
  } = useForm<IPostUsuario>({
    mode: "onBlur",
  });

  const resetForm = () => {
    reset({
      idUsuario: 0,
      nome: "",
      tipoUsuario: 0,
      abasUtilizadasIds: [],
      ativo: false,
      email: "",
      idEmpresa: 0,
    });
    setSelectedItems([]);
    setSubPermissoesTarefas([]);
  };

  const handleSwitchChange = (id: number) => {
    setSelectedItems((prev) => {
      const isSelected = prev.some((item) => item.id === id);
      if (isSelected) {
        return prev.filter((item) => item.id !== id);
      } else {
        return [...prev, { id, nome: "", disponivel: true }];
      }
    });
  };

  const handleSubmitUsuario = async (data: IPostUsuario) => {
    setIsLoadingSubmit(true);
    try {
      const postData: IPostUsuario = {
        idUsuario: data.idUsuario ?? 0,
        nome: data.nome,
        email: data.email,
        ativo: data.ativo ?? true,
        tipoUsuario: Number(data.tipoUsuario),
        idEmpresa: dataUsuario?.idEmpresa ?? Number(params.id),
        // Inclui as sub-permissões de Tarefas (101-105) somente quando a aba Tarefas (10) está marcada
        abasUtilizadasIds: [
          ...selectedItems.map((x) => x.id),
          ...(isAbaChecked(10) ? subPermissoesTarefas : []),
        ],
      };

      if (postData.idUsuario > 0) {
        await postUsuario(postData);
        openNotification({
          isOpen: true,
          message: "Usuário atualizado com sucesso!",
          type: "success",
        });
      } else {
        await postUsuario(postData);
        openNotification({
          isOpen: true,
          message: "Usuário cadastrado com sucesso!",
          type: "success",
        });
      }

      resetForm();
      onClose();
    } catch (error: any) {
      openNotification({
        isOpen: true,
        message: error?.message,
        type: "error",
      });
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const fillDataPerfilData = async (data: IGetUsuarioById) => {
    const result = await getUsuarioById(data.idUsuario);
    if (result) {
      const todasAbas = result?.data.abasUtilizadas || [];

      // Separa as sub-permissões de Tarefas (101-105) das abas comuns.
      // Filtra as indisponíveis (recurso Tarefas não liberado para a empresa) para que,
      // ao reabrir o usuário, sub-permissões indisponíveis não reapareçam habilitadas.
      const subPermissoes = todasAbas
        .filter((aba) => isSubPermissaoTarefas(aba.id) && aba.disponivel)
        .map((aba) => aba.id);
      setSubPermissoesTarefas(subPermissoes);

      const perfilAbaIds = todasAbas
        .filter((aba) => !isSubPermissaoTarefas(aba.id))
        .map((aba) => ({
          id: aba.id,
          nome: aba.nome,
          disponivel: aba.disponivel,
        }));
      setSelectedItems(perfilAbaIds);
      reset({
        idUsuario: result?.data.idUsuario,
        nome: result?.data.nome,
        email: result?.data.email,
        ativo: result?.data.ativo,
        idEmpresa: Number(data.idEmpresa),
        tipoUsuario: result?.data.tipoUsuario,
        abasUtilizadasIds: perfilAbaIds.map((aba) => aba.id),
      });
    }
  };

  const handleCheckModal = () => {
    reset({
      nome: "",
      email: "",
      tipoUsuario: 0,
      ativo: false,
      abasUtilizadasIds: [],
    });
    setIsOpenModalUnsaved(false);
    onClose();
  };

  const handleCloseModal = () => {
    resetForm();
    if (formState.isDirty) {
      setIsOpenModalUnsaved(true);
    } else {
      onClose();
    }
  };

  const handleAtivoChange = (checked: boolean) => {
    setIsAtivo(checked);
    setValue("ativo", checked);
  };
  const isAbaChecked = (value: number) => {
    return selectedItems.some((aba) => aba.id === value);
  };

  useEffect(() => {
    const fetchItems = async () => {
      const empresaId = dataUsuario?.idEmpresa || Number(params.id);
      const result = await getEmpresaById(empresaId);
      const abasUtilizadasEmpresa = result?.data?.abasUtilizadasPerfil || [];
      const abasAdicionaisEmpresa =
        result?.data?.abasUtilizadasRecursosAdicionais || [];
      // Recursos adicionais NÃO habilitados para a empresa — exibidos desabilitados
      const abasAdicionaisIndisponiveis = (
        result?.data?.abasDisponiveisRecursosAdicionais || []
      ).filter(
        (item) =>
          !abasUtilizadasEmpresa.some((aba) => aba.id === item.id) &&
          !abasAdicionaisEmpresa.some((aba) => aba.id === item.id)
      );

      const formattedItems = [
        ...abasUtilizadasEmpresa.map((item) => ({
          value: item.id,
          label: item.nome,
          disponivel: true,
        })),
        ...abasAdicionaisEmpresa.map((item) => ({
          value: item.id,
          label: item.nome,
          disponivel: true,
        })),
        ...abasAdicionaisIndisponiveis.map((item) => ({
          value: item.id,
          label: item.nome,
          disponivel: false,
        })),
      ];

      setItems(formattedItems);
    };

    fetchItems();
  }, [dataUsuario, params.id]);

  useEffect(() => {
    if (items.length > 0) {
      // Mantém selecionadas apenas as abas disponíveis (liberadas) para a empresa
      setSelectedItems((prev) =>
        prev.filter((aba) =>
          items.some((item) => item.value === aba.id && item.disponivel)
        )
      );

      // Espelha a regra acima para as sub-permissões de Tarefas (101-105): se a aba
      // Tarefas (10) não estiver disponível para a empresa, limpa as sub-permissões
      // selecionadas para que não permaneçam habilitadas indevidamente.
      if (!subPermissoesTarefasDisponiveis) {
        setSubPermissoesTarefas((prev) => (prev.length > 0 ? [] : prev));
      }
    }
  }, [items, subPermissoesTarefasDisponiveis]);

  useEffect(() => {
    if (dataUsuario) {
      fillDataPerfilData(dataUsuario);
      setSelectedItems(
        (dataUsuario.abasUtilizadas || []).filter(
          (aba) => !isSubPermissaoTarefas(aba.id)
        )
      );
      setIsAtivo(dataUsuario.ativo ?? false);
    } else {
      resetForm();
    }
  }, [dataUsuario]);

  return (
    <FormProvider
      {...{
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        getValues,
        clearErrors,
        formState,
        ...methods,
      }}
    >
      <ModalComponent
        open={isOpen}
        handleClose={handleCloseModal}
        height={"auto"}
        width={{ xs: "644px" }}
        title="Cadastro de Usuário"
        sx={{ maxHeight: "90vh" }}
        sxStackChildren={{ display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Grid container spacing={2} mb={2} py={2} px={3}>
          <Grid item xs={12} sm={6}>
            <ControlledTextField
              control={control}
              name="nome"
              label="Nome do Usuário"
              fullWidth
              rules={{ required: "Nome é obrigatório" }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledTextField
              control={control}
              name="email"
              label="E-mail"
              fullWidth
              rules={{
                required: "E-mail é obrigatório",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "E-mail inválido",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <SelectComponent
              items={tipoUsuarioEnumArray}
              control={control}
              name="tipoUsuario"
              label={"Permissão do Usuário"}
              style={{ width: "100%" }}
              rules={{ required: "Permissão é obrigatória" }}
            />
          </Grid>

          <Grid item xs={12} sm={6} display="flex" alignItems="center">
            <FormControlCustom
              label={"Ativo"}
              control={
                <Switch
                  onChange={(e) => handleAtivoChange(e.target.checked)}
                  checked={watch().ativo}
                />
              }
            />
          </Grid>
          <Grid
            item
            xs={12}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Typography
              sx={{
                fontSize: fontsSizes.medium,
                fontWeight: 400,
                color: "rgba(0, 0, 0, 0.6)",
              }}
            >
              Perfil do usuário
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Grid>

          <Grid container spacing={2} ml={1}>
            {items.map((item) => (
              <React.Fragment key={item.value}>
                <Grid item xs={12} sm={12}>
                  <FormControlCustom
                    label={item.label}
                    control={
                      <Switch
                        checked={isAbaChecked(item.value)}
                        onChange={() => handleSwitchChange(item.value)}
                        disabled={!item.disponivel}
                      />
                    }
                  />
                </Grid>
                {item.value === 10 && isAbaChecked(10) && (
                  <Grid container spacing={1} ml={4} item xs={12}>
                    {subPermissoesTarefasOpcoes.map((subPerm) => (
                      <Grid item xs={12} key={subPerm.value}>
                        <FormControlCustom
                          label={subPerm.label}
                          control={
                            <Switch
                              checked={subPermissoesTarefas.includes(subPerm.value)}
                              onChange={() => handleSubPermissaoChange(subPerm.value)}
                              disabled={!subPermissoesTarefasDisponiveis}
                            />
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </React.Fragment>
            ))}
          </Grid>
        </Grid>
        </Box>

        <Box
          display={"flex"}
          justifyContent={"flex-end"}
          p={2}
          sx={{ borderTop: "1px solid rgba(0,0,0,0.12)", flexShrink: 0 }}
        >
          <CustomButton onClick={handleCloseModal}>Cancelar</CustomButton>
          <LoadingButton
            type="submit"
            variant="contained"
            endIcon={<Save />}
            onClick={handleSubmit(handleSubmitUsuario)}
            loading={isLoadingSubmit}
            loadingPosition="end"
          >
            Salvar
          </LoadingButton>
        </Box>
      </ModalComponent>
      <ModalUnsavedEdit
        description="Ao sair todas as informações preenchidas serão apagadas, deseja continuar?"
        isOpen={isOpenModalUnsaved}
        onClose={() => setIsOpenModalUnsaved(false)}
        onYes={handleCheckModal}
        onNot={() => setIsOpenModalUnsaved(false)}
        type
      />
    </FormProvider>
  );
}
